import { PackageSize, StorageType } from '../types/shipment';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export interface SupplierIngredient {
  id: string;
  supplierId: string;
  ingredientName: string;
  storageType: StorageType;
  createdAt: string;
}

export const ingredientPackageSizeApiService = {
  /**
   * Find or create a supplier ingredient record
   * This bridges the gap between supplier.ingredientsSupplied[] (string array)
   * and the supplier_ingredients table (which has IDs for FK relationships)
   */
  findOrCreateSupplierIngredient: async (
    supplierId: string,
    ingredientName: string
  ): Promise<SupplierIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/supplier-ingredients/find-or-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ supplierId, ingredientName })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to find or create supplier ingredient');
      }

      return data.supplierIngredient;
    } catch (error) {
      console.error('Error in findOrCreateSupplierIngredient:', error);
      throw error;
    }
  },

  /**
   * Get all package sizes for a supplier ingredient
   * This is a convenience method that handles the find-or-create step internally
   */
  getPackageSizesForIngredient: async (
    supplierId: string,
    ingredientName: string
  ): Promise<{ packageSizes: PackageSize[], supplierIngredient: SupplierIngredient }> => {
    try {
      // First, find or create the supplier ingredient to get its ID
      const supplierIngredient = await ingredientPackageSizeApiService.findOrCreateSupplierIngredient(
        supplierId,
        ingredientName
      );

      // Then fetch package sizes for that supplier ingredient
      const response = await fetch(
        `${API_BASE_URL}/ingredient-package-sizes/${supplierIngredient.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch package sizes');
      }

      return { packageSizes: data.packageSizes, supplierIngredient };
    } catch (error) {
      console.error('Error fetching package sizes:', error);
      throw error;
    }
  },

  /**
   * Create a new package size for a supplier ingredient
   * This is a convenience method that handles the find-or-create step internally
   */
  createPackageSize: async (
    supplierId: string,
    ingredientName: string,
    packageSize: number,
    packageUnit: string,
    label?: string
  ): Promise<PackageSize> => {
    try {
      // First, find or create the supplier ingredient to get its ID
      const supplierIngredient = await ingredientPackageSizeApiService.findOrCreateSupplierIngredient(
        supplierId,
        ingredientName
      );

      // Then create the package size
      const response = await fetch(`${API_BASE_URL}/ingredient-package-sizes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          supplierIngredientId: supplierIngredient.id,
          packageSize,
          packageUnit,
          label: label || null
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create package size');
      }

      return data.packageSize;
    } catch (error) {
      console.error('Error creating package size:', error);
      throw error;
    }
  },

  /**
   * Delete a package size by ID
   */
  deletePackageSize: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredient-package-sizes/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete package size');
      }
    } catch (error) {
      console.error('Error deleting package size:', error);
      throw error;
    }
  },

  updateStorageType: async (supplierIngredientId: string, storageType: StorageType): Promise<SupplierIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/supplier-ingredients/${supplierIngredientId}/storage-type`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ storageType })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update storage type');
      }

      return data.supplierIngredient;
    } catch (error) {
      console.error('Error updating storage type:', error);
      throw error;
    }
  }
};
